import Lozenge from '@atlaskit/lozenge';
import Spinner from '@atlaskit/spinner';
import SharedButton from 'shared/components/buttons/button';
import dayjs from 'dayjs';
import ClassStringBuilder from 'core/utilities/class-string-builder';
import { IEligibleDriver } from 'core/api/transport-groups/transport-groups-api-interface';

/**
 * Choosing who takes a batch.
 *
 * Rows rather than a dropdown, because the choice is not between names: it is
 * between people, and where somebody was last seen is what decides it. A
 * dropdown can only show the name, which is the one piece of information that
 * does not help.
 *
 * Shared by the commit dialog and the batches list so the two cannot drift into
 * describing the same drivers differently.
 */

export const ANY_DRIVER = 'anyone';

/**
 * When a driver was last seen, dated unless it was today.
 *
 * A time on its own reads as today, and a driver whose last stop was days ago
 * would look like the nearest one to send.
 */
const lastSeen = (at: string) => {
  const when = dayjs(at);
  return when.isSame(dayjs(), 'day') ? `at ${when.format('HH:mm')}` : `on ${when.format('ddd D MMM')} at ${when.format('HH:mm')}`;
};

interface IProps {
  drivers: IEligibleDriver[];
  /**
   * Whether the list is still being fetched.
   *
   * Shown as a wait rather than as an empty list, because an empty list is an
   * answer: somebody reading "no drivers are available to name" picks *any
   * available driver* for a batch they meant to hand to a named person, and
   * nothing about the screen tells them the answer changed a second later.
   */
  loading?: boolean;
  /**
   * Why the list is missing, where it is missing for a reason.
   *
   * The same trap as loading, one layer up: falling back to an empty list makes
   * a failed lookup indistinguishable from "there is nobody", and one of those
   * is worth retrying.
   */
  error?: string;
  onRetry?: () => void;
  /** `ANY_DRIVER` for nobody in particular. */
  value: string;
  onChange: (value: string) => void;
}

const DriverPicker = ({ drivers, loading = false, error, onRetry, value, onChange }: IProps) => {
  if (loading) {
    return (
      <div data-testid='drivers-loading' className='flex flex-col items-center gap-3 py-10'>
        <Spinner size={30} />
        <p className='body-01 text-gray-600'>Finding out who is available…</p>
      </div>
    );
  }

  return (
  <div className='flex flex-col gap-2' data-testid='driver-choice'>
    {error && (
      <div data-testid='drivers-error' className='rounded border border-red-200 bg-red-50 p-3'>
        <p className='body-02 font-semibold text-red-700'>Could not load the drivers</p>
        <p className='body-01 mt-1 text-red-600'>{error}</p>
        {onRetry && (
          <div className='mt-2'>
            <SharedButton
              type='button'
              spacing='compact'
              appearance='default'
              label='Try again'
              testId='retry-drivers'
              onClick={onRetry}
            />
          </div>
        )}
      </div>
    )}

    <button
      type='button'
      data-testid='driver-anyone'
      onClick={() => onChange(ANY_DRIVER)}
      className={ClassStringBuilder([
        'rounded border p-3 text-left',
        value === ANY_DRIVER ? 'border-blue-500 bg-blue-50' : 'hover:border-blue-400',
      ])}
    >
      <p className='body-02 font-semibold'>Any available driver</p>
      <p className='body-01 mt-1 text-gray-600'>
        Every transport driver sees the batch. The first to accept it takes all of it.
      </p>
    </button>

    {drivers.map((driver) => (
      <button
        key={driver.uid}
        type='button'
        data-testid={`driver-${driver.uid}`}
        onClick={() => onChange(driver.uid)}
        className={ClassStringBuilder([
          'rounded border p-3 text-left',
          value === driver.uid ? 'border-blue-500 bg-blue-50' : 'hover:border-blue-400',
        ])}
      >
        <p className='body-02 font-semibold'>
          {driver.fullName} {driver.runName && <Lozenge appearance='inprogress'>On {driver.runName}</Lozenge>}
        </p>

        {/* Where they were last seen, which is what decides between two
            available drivers. A driver signed on but yet to finish a stop is
            not placed anywhere they have not been. */}
        {driver.lastActivity ? (
          <p className='body-01 mt-1 text-gray-600'>
            Last completed {driver.lastActivity.name}
            {driver.lastActivity.at ? ` ${lastSeen(driver.lastActivity.at)}` : ''} —{' '}
            {driver.lastActivity.address}
          </p>
        ) : (
          <p className='body-01 mt-1 text-gray-600'>
            {driver.runName ? 'No stops completed yet on this run.' : 'Not currently on a run.'}
          </p>
        )}
      </button>
    ))}

    {drivers.length === 0 && !error && (
      <p className='body-01 text-gray-600' data-testid='no-drivers'>
        No drivers are available to name. The batch can still be left for any driver to accept.
      </p>
    )}
  </div>
  );
};

export default DriverPicker;
